import { CheckBoxCard, Input, TextArea } from 'components/core';

import { useRecoilValue } from 'recoil';
import { currentDashboardState } from 'states';
import { useEffect, useState } from 'react';
import { UI_TEXT } from '../../../../../constants';
import styles from './DashboardTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';

interface FilterProps {
  register: any;
  setValue: any;
  formState: any;
  isExisting?: boolean;
}

const handleTextChange = (setValue: any) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  setValue('visualConfig', e.target.value, { shouldValidate: true });
};

const handleDateFilterToggle =
  (
    setValue: any,
    showDateFilter: boolean,
    setShowDateFilter: React.Dispatch<React.SetStateAction<boolean>>
  ) =>
  () => {
    const newValue = !showDateFilter;
    setValue('showDateFilter', newValue, { shouldValidate: true });
    setShowDateFilter(newValue);
  };

// ── Filter field documentation ────────────────────────────────────────────────
const FILTER_FIELD_DOCS = [
  {
    field: 'key',
    label: 'key',
    description:
      'The display label shown to users in the filter dropdown on the dashboard. This is what users see when they want to filter data — e.g. "District" or "Block".',
    example: '"key": "District"',
  },
  {
    field: 'values',
    label: 'values',
    description:
      'The list of options available in the filter dropdown. Each value is one option the user can select to filter the dashboard data.',
    example: '"values": ["Araria", "Arwal", "Aurangabad", "Banka", ...]',
  },
];

export const Filters = ({ register, setValue, formState, isExisting = false }: FilterProps) => {
  const currentDashboard = useRecoilValue(currentDashboardState);

  useEffect(() => {
    if (!isExisting) {
      setValue('visualConfig', UI_TEXT.DASHBOARD_TEMPLATES.VISUAL_CONFIG);
    }
  }, [currentDashboard]);
  const [showDateFilter, setShowDateFilter] = useState<boolean>(false);
  const { errors } = formState;

  useEffect(() => {
    setShowDateFilter(currentDashboard.data[0].filters.showDateFilter);
  }, [currentDashboard]);

  return (
    <div style={{ paddingBottom: '40px' }} className="col-sm-12 col-md-12 col-lg-8">
      <div className="col-sm-12 col-md-9 col-lg-9 mt-4 mb-3">
        {/* Filter field reference */}
        <div className={styles.fieldRefPanel} style={{ marginBottom: 20 }}>
          <div className={styles.fieldRefHeader}>
            <span className="material-icons" style={{ fontSize: 15, color: 'var(--primary-1)' }}>
              menu_book
            </span>
            <span className={styles.fieldRefTitle}>Filter field reference</span>
            <span className={styles.fieldRefSub}>
              Hover over a field name for help, or click to keep it open
            </span>
          </div>
          <div className={styles.fieldRefGrid}>
            {FILTER_FIELD_DOCS.map((doc) => (
              <div key={doc.field} className={styles.fieldRefItem}>
                <div className={styles.fieldRefItemTop}>
                  <code className={styles.fieldRefCode}>{doc.label}</code>
                  <FieldTooltip
                    title={doc.label}
                    description={doc.description}
                    example={doc.example}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Global filters textarea */}
        <div className="mb-4">
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Global filters</span>
            <FieldTooltip
              title="Global filters"
              description="A JSON array that defines the filter options shown to users at the top of the dashboard. Each filter has a key (the label shown to users) and a values array (the list of options they can pick from). These filters apply to all charts on the dashboard at once."
              example='[{"key": "District", "values": ["Araria","Arwal","Aurangabad",...]}, {"key": "Block", "groups": [{"name": "Patna", "items": ["Patna Sadar","Phulwari",...]}]}]'
              note="Must be valid JSON. Each object in the array creates one filter dropdown on the dashboard."
            />
          </div>
          <div className={styles.fieldHint}>
            These filters will let users filter data at a dashboard level.
          </div>

          {/* Inline example — always visible, no tooltip needed */}
          {!isExisting && (
            <div className={styles.jsonNote}>
              <span className="material-icons" style={{ fontSize: 16, flexShrink: 0 }}>
                info
              </span>
              <strong>
                ✏️ New dashboard — the JSON below is a starter template. Edit it before saving.
              </strong>
            </div>
          )}
          <TextArea
            rows={10}
            showTitle={false}
            showDescription={false}
            title=""
            {...register('visualConfig')}
            className={`${errors.visualConfig ? 'is-invalid' : ''}`}
            onChange={handleTextChange(setValue)}
          />
          {errors.visualConfig && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errors.visualConfig.message}
            </div>
          )}
        </div>

        {/* Date filter toggle */}
        <div className="mb-4">
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Date filter</span>
            <FieldTooltip
              title="Show date filter"
              description="When enabled, a date range picker appears at the top of the dashboard allowing users to filter all charts by a specific time period — for example, viewing data only for March 2026. The date field used for filtering is defined in each visualization's query configuration."
            />
          </div>
          <CheckBoxCard
            isChecked={showDateFilter}
            label={'Show date filter'}
            clickHandler={handleDateFilterToggle(setValue, showDateFilter, setShowDateFilter)}
          />
          <Input type="hidden" {...register('showDateFilter')} />
        </div>
      </div>
    </div>
  );
};